import { differenceInCalendarDays } from 'date-fns';

import type { TopClientsDatum } from '../types';

export type TopClientsQuery = {
  /** ISO yyyy-MM-dd, inclusive. */
  from: string;
  /** How many clients to return, largest `booked` first. */
  limit: number;
  /** ISO yyyy-MM-dd, inclusive. */
  to: string;
};

/** Shaped like a future API response so a real fetch can drop in without changing call sites. */
export type TopClientsResponse = {
  data: TopClientsDatum[];
};

export const TOP_CLIENTS_WIDGET_LIMIT = 5;
export const TOP_CLIENTS_LIMIT_OPTIONS = [5, 10, 15] as const;
export const DEFAULT_TOP_CLIENTS_DETAIL_LIMIT = 10;

/** Yearly baseline per client; scaled to the requested range length. */
const CLIENTS: TopClientsDatum[] = [
  { booked: 380_000, budgeted: 180_000, label: 'Zurich', quoted: 220_000, revenue: 90_000 },
  { booked: 150_000, budgeted: 220_000, label: 'Nestlé', quoted: 80_000, revenue: 60_000 },
  { booked: 60_000, budgeted: 160_000, label: 'Roche', quoted: 140_000, revenue: 40_000 },
  { booked: 540_000, budgeted: 460_000, label: 'Novartis', quoted: 330_000, revenue: 170_000 },
  { booked: 500_000, budgeted: 100_000, label: 'UBS', quoted: 200_000, revenue: 40_000 },
  { booked: 210_000, budgeted: 300_000, label: 'Swiss Re', quoted: 260_000, revenue: 130_000 },
  { booked: 320_000, budgeted: 250_000, label: 'ABB', quoted: 180_000, revenue: 100_000 },
  { booked: 90_000, budgeted: 220_000, label: 'Credit Suisse', quoted: 150_000, revenue: 50_000 },
  { booked: 260_000, budgeted: 180_000, label: 'Adecco', quoted: 140_000, revenue: 70_000 },
  { booked: 130_000, budgeted: 160_000, label: 'Logitech', quoted: 90_000, revenue: 40_000 },
  { booked: 410_000, budgeted: 300_000, label: 'Richemont', quoted: 250_000, revenue: 140_000 },
  { booked: 180_000, budgeted: 260_000, label: 'Lonza', quoted: 220_000, revenue: 90_000 },
  { booked: 240_000, budgeted: 200_000, label: 'Givaudan', quoted: 160_000, revenue: 80_000 },
  { booked: 110_000, budgeted: 140_000, label: 'Swisscom', quoted: 100_000, revenue: 50_000 },
  { booked: 70_000, budgeted: 120_000, label: 'Sika', quoted: 60_000, revenue: 30_000 },
];

function roundToThousand(value: number) {
  return Math.round(value / 1_000) * 1_000;
}

/** Deterministic mock so the chart looks the same on every render. */
export function generateTopClientsData({ from, to, limit }: TopClientsQuery): TopClientsResponse {
  const start = new Date(`${from}T00:00:00`);
  const end = new Date(`${to}T00:00:00`);
  const days = Math.max(0, differenceInCalendarDays(end, start) + 1);
  const scale = days / 365;
  // Days since epoch of the range start — so ranges of equal length (e.g.
  // "this year" vs "last year") still rank clients differently.
  const seed = Math.floor(start.getTime() / 86_400_000);

  const data = CLIENTS.map((client, index) => {
    const variation = 1 + 0.3 * Math.sin(seed * 0.37 + index * 1.7);

    return {
      booked: roundToThousand(client.booked * scale * variation),
      budgeted: roundToThousand(client.budgeted * scale),
      label: client.label,
      quoted: roundToThousand(client.quoted * scale * variation),
      revenue: roundToThousand((client.revenue ?? 0) * scale * variation),
    };
  });

  return {
    data: data.sort((a, b) => b.booked - a.booked).slice(0, limit),
  };
}
